import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { authApi } from '../../api/endpoints/auth';
import { useAuthStore } from '../../stores/authStore';
import { useLoginFlowStore } from '../../stores/loginFlowStore';
import { useOtpCooldown } from '../../hooks/useOtpCooldown';
import { handleApiError } from '../../api/errorMapping';
import { ApiError } from '../../api/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { TextField } from '../ui/TextField';
import { LoadingButton } from '../ui/LoadingButton';
import { Button } from '@/kit';
import { Banner } from '../ui/Banner';
import {
    CHALLENGE_SESSION_REJECTED_CODE,
    EMAIL_OTP_LENGTH,
    INVALID_OTP_ERROR_CODES,
    formatTime,
    maskEmail,
} from '../../lib/otp';

interface Props {
    /** Fallback de la URL (deprecado): la fuente de verdad es el store. */
    session?: string;
    /** Fallback de la URL: el tipo se deriva del store (challengeType). */
    type?: 'TOTP' | 'EMAIL' | 'SMS';
    /** Fallback de la URL. */
    email?: string;
    /** Fallback de la URL. */
    tenantId?: string;
}

/**
 * Pantalla del challenge MFA/OTP del login (DEC-RBAC-054/055).
 *
 * - Lee `session`/`email`/`tenant`/`tipo` del store de flujo (search params quedan como fallback
 *   deprecado para no romper deep-links viejos).
 * - "Reenviar código" REAL: `POST /auth/resend-otp { email, tenantId }` → nuevo session+OTP sin
 *   password; con la respuesta hace `setSession` + `markSent()` (resetea el cooldown).
 * - "Atrás" → vuelve al paso password con email/tenant preservados (no a login vacío).
 * - "Cancelar" → limpia el store y vuelve al login limpio (separado del "atrás").
 * - Cooldown refresh-proof vía `useOtpCooldown('login')`.
 */
export function MfaChallengeForm({
    session: sessionFromUrl,
    type: typeFromUrl,
    email: emailFromUrl,
    tenantId: tenantIdFromUrl,
}: Props) {
    const { t } = useTranslation(['auth']);
    const navigate = useNavigate();
    const refresh = useAuthStore((s) => s.refresh);

    // Store = fuente de verdad; la URL queda como fallback (deep-links viejos).
    const storeSession = useLoginFlowStore((s) => s.session);
    const storeEmail = useLoginFlowStore((s) => s.email);
    const storeTenantId = useLoginFlowStore((s) => s.tenantId);
    const challengeType = useLoginFlowStore((s) => s.challengeType);
    const setSession = useLoginFlowStore((s) => s.setSession);
    const back = useLoginFlowStore((s) => s.back);
    const reset = useLoginFlowStore((s) => s.reset);

    const session = storeSession ?? sessionFromUrl ?? '';
    const email = storeEmail || emailFromUrl;
    const tenantId = storeTenantId ?? tenantIdFromUrl;
    // DEC-RBAC-084: el sub-challenge puede ser SMS_OTP (3er método) además de EMAIL_OTP / TOTP.
    const type: 'TOTP' | 'EMAIL' | 'SMS' =
        challengeType === 'SOFTWARE_TOKEN_MFA'
            ? 'TOTP'
            : challengeType === 'SMS_OTP'
              ? 'SMS'
              : (typeFromUrl ?? 'EMAIL');

    const [code, setCode] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(null);

    const { secondsLeft, canResend, markSent } = useOtpCooldown('login');

    const verifyMutation = useMutation({
        mutationFn: () => {
            if (!email) {
                throw new Error('Email no disponible — vuelve a la pantalla de login.');
            }
            return authApi.challenge({
                email,
                session,
                // Tenant del picker (multipool): el backend lo usa para resolver el pool correcto.
                tenantId: tenantId ?? undefined,
                challengeType:
                    type === 'TOTP'
                        ? 'SOFTWARE_TOKEN_MFA'
                        : type === 'SMS'
                          ? 'SMS_OTP'
                          : 'EMAIL_OTP',
                // CUSTOM_AUTH (DEC-AUTH-013): Cognito SIEMPRE lee el código desde
                // ChallengeResponses.ANSWER (el trigger VerifyAuthChallengeResponse lo recibe
                // como event.request.challengeAnswer). El sub-tipo EMAIL/TOTP es solo display.
                challengeResponses: { ANSWER: code },
            });
        },
        onSuccess: async (resp) => {
            setError(null);
            // Shape real del response: { ok: true, challenge?: {...} }. Si NO hay challenge,
            // significa que el flow terminó OK con tokens — limpiamos el store, refrescamos auth
            // y vamos al dashboard.
            if (!resp.challenge) {
                reset();
                await refresh();
                await navigate({ to: '/' });
                return;
            }
            // El backend reintenta hasta 3 veces: ante un código incorrecto con intentos restantes
            // devuelve un NUEVO challenge con nueva session. Refrescamos session, limpiamos el campo
            // y le pedimos al user que reingrese el código.
            setSession(resp.challenge.session);
            setCode('');
            setError(
                type === 'EMAIL'
                    ? 'Código incorrecto. Vuelve a ingresar el código que te enviamos por correo.'
                    : type === 'SMS'
                      ? 'Código incorrecto. Vuelve a ingresar el código que te enviamos por SMS.'
                      : 'Código incorrecto. Ingresa el código actual de tu app de autenticación.',
            );
        },
        // Solo banner: el toast de handleApiError duplicaba el mensaje en los errores COGNITO_*.
        onError: (err) => {
            if (err instanceof ApiError) {
                if (err.code === CHALLENGE_SESSION_REJECTED_CODE) {
                    // Reto agotado (3 intentos) o session de Cognito vencida: se vuelve a la contraseña.
                    toast.error(t('auth:mfa.challenge.exhausted'));
                    back();
                    void navigate({ to: '/login' });
                    return;
                }
                if (INVALID_OTP_ERROR_CODES.has(err.code)) {
                    setError(t('auth:mfa.challenge.invalidCode'));
                    return;
                }
                setError(t('auth:mfa.challenge.genericError'));
                return;
            }
            setError(t('auth:mfa.challenge.networkError'));
        },
    });

    // Reenvío REAL en login (pre-token). EMAIL_OTP y SMS_OTP van por el MISMO camino:
    // `authApi.resendOtp({email,tenantId})` re-dispara CUSTOM_AUTH sin password y devuelve una session
    // fresca. El CANAL del OTP NO lo elige el front — lo decide el trigger CreateAuthChallenge del
    // connector según el `mfaMethod` del user (DEC-RBAC-083): re-enviar re-corre el preparer, que manda
    // por SMS si el método es SMS_OTP. `meApi.requestSmsOtp()` NO se usa acá: es autenticado (self-service
    // /me), inválido en el contexto pre-token del challenge de login. TOTP no tiene reenvío.
    const resendMutation = useMutation({
        mutationFn: async (): Promise<{ session?: string }> => {
            if (!email || !tenantId) {
                throw new Error('Faltan datos para reenviar — vuelve al login.');
            }
            return authApi.resendOtp({ email, tenantId });
        },
        onSuccess: (resp) => {
            setError(null);
            // Anti-enumeración (EMAIL): si el (email,tenant) no era válido el backend devuelve {} (sin
            // session). Igual reseteamos el cooldown y mostramos el mensaje genérico para no
            // filtrar existencia. Si vino session fresca, la adoptamos (la vieja se invalida).
            if (resp.session) {
                setSession(resp.session);
            }
            markSent();
            setInfo(
                type === 'SMS'
                    ? 'Te enviamos un código nuevo por SMS.'
                    : 'Si tu correo está registrado, te enviamos un código nuevo.',
            );
        },
        onError: (err) => {
            handleApiError(err);
            setInfo(
                'No pudimos reenviar el código ahora. Espera unos segundos e intenta de nuevo.',
            );
        },
    });

    // SMS OTP comparte la longitud del OTP por correo (no-TOTP → 4 dígitos).
    const codeMaxLen = type === 'TOTP' ? 6 : EMAIL_OTP_LENGTH;
    // El reenvío en login aplica a EMAIL y SMS por el mismo camino (resend-otp con email+tenant; el
    // canal lo decide el connector). TOTP no tiene reenvío (lo genera la app del user). Nota: para users
    // phone-primary sin email, resend-otp (email-based) no aplica aún — gap del dual-identifier, no del SMS.
    const canDoResend = canResend && (type === 'EMAIL' || type === 'SMS') && !!email && !!tenantId;

    const heading =
        type === 'EMAIL'
            ? 'Verifica tu correo'
            : type === 'SMS'
              ? 'Verifica tu teléfono'
              : 'Ingresa tu código MFA';
    const description =
        type === 'EMAIL'
            ? email
                ? `Te enviamos un código de verificación a ${maskEmail(email)}. Vence en 2 minutos.`
                : 'Te enviamos un código de verificación a tu correo. Vence en 2 minutos.'
            : type === 'SMS'
              ? 'Te enviamos un código por SMS a tu teléfono. Vence en 2 minutos.'
              : 'Abre tu app de autenticación y copia el código de 6 dígitos.';

    // "Atrás": volvemos al paso password preservando email/tenant (el store lo hace). No limpia
    // el flujo — solo retrocede un paso.
    const goBack = (): void => {
        back();
        void navigate({ to: '/login' });
    };

    // "Cancelar": descarta TODO el flujo y vuelve al login limpio (separado del "atrás").
    const cancel = (): void => {
        reset();
        void navigate({ to: '/login' });
    };

    return (
        <Card className="w-full max-w-md shadow-lg">
            <CardHeader className="space-y-1 text-center">
                <CardTitle className="text-2xl">{heading}</CardTitle>
                <CardDescription>{description}</CardDescription>
            </CardHeader>
            <CardContent>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        setError(null);
                        verifyMutation.mutate();
                    }}
                    className="space-y-4"
                    noValidate
                >
                    {error && (
                        <Banner
                            variant="error"
                            dismissible
                            onDismiss={() => {
                                setError(null);
                            }}
                        >
                            {error}
                        </Banner>
                    )}
                    {info && (
                        <Banner
                            variant="info"
                            dismissible
                            onDismiss={() => {
                                setInfo(null);
                            }}
                        >
                            {info}
                        </Banner>
                    )}
                    <TextField
                        label={
                            type === 'EMAIL'
                                ? 'Código del correo'
                                : type === 'SMS'
                                  ? 'Código del SMS'
                                  : 'Código del autenticador'
                        }
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={codeMaxLen}
                        placeholder={'•'.repeat(codeMaxLen)}
                        value={code}
                        onChange={(e) => {
                            setCode(e.target.value.replace(/\D/g, ''));
                        }}
                        // OTP input: autofocus es buen UX y la pantalla es de un solo campo.
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus
                    />
                    <LoadingButton
                        type="submit"
                        className="w-full"
                        isLoading={verifyMutation.isPending}
                        loadingText="Verificando…"
                        disabled={code.length !== codeMaxLen}
                    >
                        {t('auth:mfa.challenge.submit')}
                    </LoadingButton>
                </form>

                {(type === 'EMAIL' || type === 'SMS') && (
                    <div className="mt-6 pt-4 border-t text-center text-sm space-y-2">
                        {secondsLeft > 0 ? (
                            <p className="text-muted-foreground">
                                Puedes pedir un código nuevo en{' '}
                                <span className="font-mono font-semibold">
                                    {formatTime(secondsLeft)}
                                </span>
                            </p>
                        ) : (
                            <p className="text-muted-foreground">¿No te llegó el código?</p>
                        )}
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={!canDoResend || resendMutation.isPending}
                            onClick={() => {
                                setInfo(null);
                                resendMutation.mutate();
                            }}
                        >
                            Reenviar código
                        </Button>
                    </div>
                )}

                {/* "Atrás" (vuelve al paso password preservando email/tenant) separado de "Cancelar"
                    (limpia el flujo y vuelve al login limpio). */}
                <div className="mt-4 flex items-center justify-between">
                    <button
                        type="button"
                        onClick={goBack}
                        className="inline-flex items-center text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                    >
                        <ArrowLeft className="mr-1 h-3.5 w-3.5" aria-hidden />
                        Volver
                    </button>
                    <button
                        type="button"
                        onClick={cancel}
                        className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                    >
                        Cancelar
                    </button>
                </div>
            </CardContent>
        </Card>
    );
}
